import React, { useState } from 'react';

import {
  faCheckDouble,
  faCopy,
  faThumbsDown,
  faThumbsUp,
} from '@fortawesome/pro-light-svg-icons';
import {
  faThumbsDown as faSolidThumbsDown,
  faThumbsUp as faSolidThumbsUp,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch } from 'react-redux';

import NeoLeoAvatar from '../../../assets/img/neo-leo.png';
import AnalyticsService from '../../../services/AnalyticsService';
import { updateMessageVote } from '../../../slices/LeoSlice';
import { AnalyticsEventEnum, AppDispatch, CurrentChat } from '../../../types';
import { VoteType } from '../../../utils/LeoUtils';

interface NeoLeoChatResponseActionsProps extends CurrentChat {}

enum ResponseActionsEnum {
  // REGENERATE = 'Regenerate',
  UPVOTE = 'Upvote',
  DOWNVOTE = 'Downvote',
  COPY = 'Copy',
}

interface ResponseActionsEnumAndLoadingMap {
  // [ResponseActionsEnum.REGENERATE]: boolean;
  [ResponseActionsEnum.UPVOTE]: boolean;
  [ResponseActionsEnum.DOWNVOTE]: boolean;
  [ResponseActionsEnum.COPY]: boolean;
}

const DEFAULT_RESPONSE_ACTIONS_LOADING_MAP_STATE = {
  // [ResponseActionsEnum.REGENERATE]: false,
  [ResponseActionsEnum.UPVOTE]: false,
  [ResponseActionsEnum.DOWNVOTE]: false,
  [ResponseActionsEnum.COPY]: false,
};

const NeoLeoChatResponseActions: React.FC<NeoLeoChatResponseActionsProps> = (
  msgRes,
) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isCopied, setIsCopied] = useState(false);
  const [responseActionsLoadingMap, setResponseActionsLoadingMap] =
    useState<ResponseActionsEnumAndLoadingMap>(
      DEFAULT_RESPONSE_ACTIONS_LOADING_MAP_STATE,
    );

  const onCopyText = (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      new Promise<void>((res, rej) => {
        document.execCommand('copy') ? res() : rej();
        textArea.remove();
      });
    }
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  const setActionLoading = (
    actionType: ResponseActionsEnum,
    loading: boolean,
  ) => {
    setResponseActionsLoadingMap({
      ...responseActionsLoadingMap,
      [actionType]: loading,
    });
  };

  const actions = [
    // {
    //   actionType: RESPONSE_ACTIONS_ENUM.REGENERATE,
    //   icon: faRotate,
    //   onClick: () => {},
    // },
    {
      actionType: ResponseActionsEnum.UPVOTE,
      icon: msgRes.vote_type === VoteType.Upvote ? faSolidThumbsUp : faThumbsUp,
      onClick: async () => {
        setActionLoading(ResponseActionsEnum.UPVOTE, true);
        await dispatch(updateMessageVote(msgRes, VoteType.Upvote));
        setActionLoading(ResponseActionsEnum.UPVOTE, false);
        AnalyticsService.instance().logEvent(
          AnalyticsEventEnum.LEO_RESPONSE_THUMBS_UP_CLICK,
        );
      },
    },
    {
      actionType: ResponseActionsEnum.DOWNVOTE,
      icon:
        msgRes.vote_type === VoteType.Downvote
          ? faSolidThumbsDown
          : faThumbsDown,
      onClick: async () => {
        setActionLoading(ResponseActionsEnum.DOWNVOTE, true);
        await dispatch(updateMessageVote(msgRes, VoteType.Downvote));
        setActionLoading(ResponseActionsEnum.DOWNVOTE, false);
        AnalyticsService.instance().logEvent(
          AnalyticsEventEnum.LEO_RESPONSE_THUMBS_DOWN_CLICK,
        );
      },
    },
    {
      actionType: ResponseActionsEnum.COPY,
      icon: faCopy,
      onClick: () => {
        onCopyText(msgRes.message!);
        AnalyticsService.instance().logEvent(
          AnalyticsEventEnum.LEO_RESPONSE_COPY_CLICK,
        );
      },
    },
  ];

  return (
    <div className='relative'>
      <div className='h-[28px] flex flex-row items-center'>
        <img
          className='w-[26px] h-[26px] mr-3'
          src={NeoLeoAvatar}
          alt='neo-leo-avatar'
        />
        {actions.map(({ icon, actionType, onClick }) => (
          <button
            type='button'
            className='appearance-none flex flex-row items-center w-6 mr-2.5 transition-all duration-200 ease-in-out transform hover:scale-110'
            key={actionType}
            onClick={onClick}
            disabled={responseActionsLoadingMap[actionType]}
            title={actionType}
          >
            <FontAwesomeIcon
              icon={icon}
              className='text-base transition-all duration-200 ease-in-out text-leo-response-action hover:text-white'
            />
          </button>
        ))}
      </div>
      {isCopied && (
        <div className='absolute w-full -top-[25px] flex flex-row justify-end items-center'>
          <div className='rounded-lg bg-[#282932] flex flex-row items-center px-1.5 py-1 animate-fade-in'>
            <FontAwesomeIcon
              icon={faCheckDouble}
              className='text-[#A0A0A0] text-sm'
            />
            <p className='text-xs text-[#A0A0A0] ml-1'>Copied</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default NeoLeoChatResponseActions;
